import { useState } from "react";
import { useFormik } from "formik";
import { Dialog, DialogContent, DialogActions, TextField, Button, Box, MenuItem } from "@mui/material";
import { P } from "theme/typography";
import RequestStatus from "redux/slices/RequestStatus";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectLensEditorLens, updateLensInfo } from "redux/slices/lensEditor";
import { useSnackbar } from "notistack";
import { parseSafeInt } from "Backend";
import { LensBillingStatus } from "generated/graphql";
import PSelect from "components/PSelect";

const ALL_LENS_BILLING_STATUSES: { name: string; value: LensBillingStatus }[] = Object.entries(LensBillingStatus).map(
  ([name, value]) => ({
    name: name,
    value: value,
  })
);

interface LensInfoEditDialogProps {
  onClose: () => void;
}

const LensInfoEditDialog = ({ onClose }: LensInfoEditDialogProps) => {
  const [updatePending, setUpdatePending] = useState<RequestStatus>(RequestStatus.IDLE);
  const lensEditorLens = useAppSelector(selectLensEditorLens);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      name: lensEditorLens.name,
      externalId: lensEditorLens.externalId,
      description: lensEditorLens.description,
      litigationName: lensEditorLens.litigationName,
      rank: lensEditorLens.rank,
      billingStatus: lensEditorLens.billingStatus,
    },
    onSubmit: (values) => {
      setUpdatePending(RequestStatus.PENDING);
      dispatch(
        updateLensInfo({
          id: lensEditorLens.id,
          name: values.name,
          externalId: values.externalId ?? undefined,
          clearExternalId: values.externalId === "" ? true : false,
          description: values.description,
          litigationName: values.litigationName ?? undefined,
          rank: values.rank,
          billingStatus: values.billingStatus,
        })
      )
        .unwrap()
        .then(() => {
          setUpdatePending(RequestStatus.SUCCEEDED);
          onClose();
          enqueueSnackbar("Lens info updated", { variant: "success" });
        })
        .catch(() => {
          setUpdatePending(RequestStatus.FAILED);
          enqueueSnackbar("Failed to update lens info", { variant: "error" });
          console.error("Failed to update lens info");
        });
    },
  });

  return (
    <Dialog open={true} fullWidth maxWidth="sm">
      <DialogContent>
        <Box
          sx={{
            mb: 2,
          }}>
          <P variant="xlBold">Edit Lens Info</P>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}>
          <TextField
            label="Lens Name"
            required
            value={formik.values.name}
            onChange={formik.handleChange}
            fullWidth
            name="name"
            error={Boolean(formik.errors.name)}
            helperText={formik.errors.name}
          />
          <TextField
            label="External ID"
            value={formik.values.externalId}
            onChange={formik.handleChange}
            fullWidth
            name="externalId"
          />
          <TextField
            label="Lens Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            name="description"
          />
          <TextField
            label="Litigation Name"
            value={formik.values.litigationName}
            onChange={formik.handleChange}
            name="litigationName"
          />
          <TextField
            name="rank"
            value={formik.values.rank}
            onChange={(event) => {
              const value = event.target.value;
              formik.setFieldValue("rank", parseSafeInt(value) ?? 0);
            }}
            label="Rank"
          />
          <PSelect
            name="billingStatus"
            value={formik.values.billingStatus ?? ""}
            onChange={formik.handleChange}
            label="Billing Status">
            <MenuItem value="">Unset</MenuItem>
            {ALL_LENS_BILLING_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.name}
              </MenuItem>
            ))}
          </PSelect>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
          }}
          disabled={updatePending === RequestStatus.PENDING}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!formik.dirty || updatePending === RequestStatus.PENDING || Boolean(formik.errors.name)}
          variant="contained"
          onClick={() => formik.handleSubmit()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LensInfoEditDialog;
